import React, { useMemo, useState } from "react";
import { api } from "../utils/api";
import { useToast, colors } from "@forge/ui";

type Booking = {
  id: string;
  date: string; // ISO date
  time: string;
  trainer?: { name?: string };
  status: "pending" | "confirmed" | "cancelled" | "completed";
  notes?: string;
};

type Props = {
  booking: Booking;
  onRefresh?: () => void;
};

async function cancelBooking(id: string) {
  return api.put<unknown>(`/api/user/bookings/${id}`, { status: "cancelled" });
}

const BookingCard: React.FC<Props> = ({ booking, onRefresh }) => {
  const [expanded, setExpanded] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const { show } = useToast();
  const canCancel = useMemo(() => {
    // Simple 24h policy check based on date/time strings
    const sessionDate = new Date(booking.date + "T" + booking.time);
    const diffHr =
      (sessionDate.getTime() - new Date().getTime()) / (1000 * 60 * 60);
    return (
      booking.status !== "cancelled" &&
      booking.status !== "completed" &&
      diffHr >= 24
    );
  }, [booking]);

  const handleCancel = async () => {
    if (!canCancel) return;
    try {
      await cancelBooking(booking.id);
      show("Booking cancelled", "success");
      setConfirming(false);
      onRefresh?.();
    } catch (e) {
      show(e instanceof Error ? e.message : "Cancel failed", "error");
    }
  };

  return (
    <div className={`fp-booking-card ${booking.status}`}>
      <button
        type="button"
        className="fp-card-summary"
        onClick={() => setExpanded((s) => !s)}
        aria-expanded={expanded}
        style={{
          width: "100%",
          background: "none",
          border: "none",
          color: "inherit",
          textAlign: "left",
          font: "inherit",
        }}
      >
        <div className="fp-card-title">
          {booking.trainer?.name ?? "Trainer"} •{" "}
          {new Date(booking.date).toLocaleDateString()} at {booking.time}
        </div>
        <div className="fp-card-status">{booking.status}</div>
      </button>
      {expanded && (
        <div className="fp-card-details">
          <p>
            <strong>Date:</strong>{" "}
            {new Date(booking.date + "T" + booking.time).toLocaleString()}
          </p>
          {booking.notes && (
            <p>
              <strong>Notes:</strong> {booking.notes}
            </p>
          )}
          <p className="fp-policy">
            Policy: You can cancel up to 24 hours before the session.
          </p>
          {canCancel ? (
            confirming ? (
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <span style={{ fontSize: 13, color: colors.textMuted }}>Cancel this booking?</span>
                <button className="cta secondary" onClick={handleCancel}>
                  Yes, cancel
                </button>
                <button
                  className="cta secondary"
                  onClick={() => setConfirming(false)}
                  style={{ background: colors.surfaceElevated, color: colors.text, border: `1px solid ${colors.border}` }}
                >
                  Keep booking
                </button>
              </div>
            ) : (
              <button className="cta secondary" onClick={() => setConfirming(true)}>
                Cancel Booking
              </button>
            )
          ) : (
            <span className="muted">
              Cancellation not available within 24 hours.
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default BookingCard;
